import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  DocumentTextIcon,
  PaperClipIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  AcademicCapIcon,
  XMarkIcon,
  CheckIcon,
  PencilIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import CertificateDownload from './CertificateDownload';

const CertificateAssignment = ({ courseId, course }) => {
  const { user } = useAuth();
  const [assignment, setAssignment] = useState(null);
  const [studentSubmission, setStudentSubmission] = useState(null);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseProgress, setCourseProgress] = useState(null);

  const [canManageAssignments] = useState(user?.role === 'admin' || (user?.role === 'instructor' && course?.instructor?._id === user?._id));

  // Modal states
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [showGradingModal, setShowGradingModal] = useState(false);
  const [showSubmissionDetailsModal, setShowSubmissionDetailsModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // Form states
  const [submissionText, setSubmissionText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [gradingForm, setGradingForm] = useState({
    percentage: '',
    feedback: '',
    allowCertificateDownload: false
  });

  useEffect(() => {
    if (user) {
      fetchCertificateAssignment();
      if (user?.role === 'student') {
        fetchCourseProgress();
      }
    }
  }, [courseId, user]);

  const fetchCourseProgress = async () => {
    try {
      const response = await axios.get(`/api/course-progress/${courseId}`);
      setCourseProgress(response.data);
    } catch (error) {
      console.error('Error fetching course progress:', error);
    }
  };

  const fetchCertificateAssignment = async () => {
    try {
      const response = await axios.get(`/api/courses/${courseId}/certificate-assignment`);
      setAssignment(response.data.assignment);
      setStudentSubmission(response.data.studentSubmission);
      setAllSubmissions(response.data.allSubmissions || []);

    } catch (error) {
      console.error('Error fetching certificate assignment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    const oversized = files.filter(file => file.size > maxSize);
    if (oversized.length > 0) {
      toast.error('Each file must be less than 10MB');
      return;
    }

    setAttachments(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!submissionText.trim() && attachments.length === 0) {
      toast.error('Please provide either text or files for your submission');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('submissionText', submissionText);

      attachments.forEach(file => {
        formData.append('files', file);
      });


      await axios.post(`/api/courses/${courseId}/certificate-submission`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Submission successful! You can view your grade once it\'s processed.');
      setShowSubmissionModal(false);
      setSubmissionText('');
      setAttachments([]);
      fetchCertificateAssignment();
    } catch (error) {
      toast.error('Failed to submit assignment');
      console.error('Submission error:', error);
    }
  };

  const handleGrade = async (e) => {
    e.preventDefault();

    if (!gradingForm.percentage || gradingForm.percentage < 0 || gradingForm.percentage > 100) {
      toast.error('Please enter a valid percentage (0-100)');
      return;
    }

    try {

      await axios.put(`/api/courses/${courseId}/certificates/${selectedSubmission.student._id}/grade`, gradingForm);

      toast.success('Grade submitted successfully!');
      setShowGradingModal(false);
      setSelectedSubmission(null);
      setGradingForm({ percentage: '', feedback: '', allowCertificateDownload: false });
      fetchCertificateAssignment();
    } catch (error) {
      toast.error('Failed to submit grade');
    }
  };

  const openSubmissionModal = () => {
    setShowSubmissionModal(true);
  };

  const openGradingModal = (submission) => {
    setSelectedSubmission(submission);
    setGradingForm({
      percentage: submission.grade?.percentage || '',
      feedback: submission.grade?.feedback || '',
      allowCertificateDownload: submission.allowCertificateDownload || false
    });
    setShowGradingModal(true);
  };

  const openSubmissionDetailsModal = (submission) => {
    setSelectedSubmission(submission);
    setShowSubmissionDetailsModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'submitted': return 'text-blue-600 bg-blue-100';
      case 'graded': return 'text-purple-600 bg-purple-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const canDownloadCertificate = studentSubmission?.status === 'approved' && studentSubmission?.allowCertificateDownload;

  if (loading) {
    return (
      <div className="card">
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading certificate requirements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Certificate Assignment Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <AcademicCapIcon className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Certificate Requirements</h2>
          </div>
        </div>

        {/* Assignment Display - Only show when course is 100% complete for students */}
        {assignment && (user?.role !== 'student' || (user?.role === 'student' && courseProgress && courseProgress.overallProgress >= 100)) ? (
          <div>
            <div className="border-l-4 border-blue-500 pl-4 mb-6">
              <h3 className="text-lg font-medium text-gray-900">{assignment.title}</h3>
              <p className="text-gray-600 mt-1">{assignment.description}</p>
              <p className="text-sm text-gray-500 mt-2">
                Passing Score: {assignment.passingScore}% or higher
              </p>

              {/* Display assignment files */}
              {assignment.attachments && assignment.attachments.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Assignment Files:</p>
                  <div className="flex flex-wrap gap-2">
                    {assignment.attachments.map((file, index) => (
                      <a
                        key={index}
                        href={`${file.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-2 rounded border hover:bg-blue-100 transition-colors"
                      >
                        <PaperClipIcon className="h-4 w-4" />
                        <span className="text-sm">{file.filename}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Student View */}
            {user?.role === 'student' && (
              <div className="space-y-4">
                {/* Course Progress Check */}
                {courseProgress && courseProgress.overallProgress < 100 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mr-3" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-2">Complete Course First</p>
                        <p className="mb-2">Your current progress: <span className="font-semibold">{courseProgress.overallProgress.toFixed(1)}%</span></p>
                        <p>You need to complete 100% of the course content before you can access assignments and certificate downloads.</p>
                      </div>
                    </div>
                  </div>
                )}

                {courseProgress && courseProgress.overallProgress === 100 ? (
                  <>
                    {studentSubmission ? (
                      <div className="space-y-4">
                        {/* Submission Content */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-medium text-gray-900 mb-3">Your Submission</h4>
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-3 ${getStatusColor(studentSubmission.status)}`}>
                            {studentSubmission.status}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            Submitted on {new Date(studentSubmission.submittedAt).toLocaleDateString()}
                          </p>

                          {studentSubmission.submissionText && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Your Response:</p>
                              <div className="bg-white p-3 rounded border max-h-60 overflow-y-auto">
                                <p className="whitespace-pre-wrap text-sm">{studentSubmission.submissionText}</p>
                              </div>
                            </div>
                          )}

                          {studentSubmission.attachments && studentSubmission.attachments.length > 0 && (
                            <div className="mb-4">
                              <p className="text-sm font-medium text-gray-700 mb-2">Your Files:</p>
                              <div className="space-y-2">
                                {studentSubmission.attachments.map((file, index) => (
                                  <a
                                    key={index}
                                    href={`${file.path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 bg-blue-50 text-blue-700 px-4 py-2 rounded border hover:bg-blue-100 transition-colors"
                                  >
                                    <PaperClipIcon className="h-4 w-4" />
                                    <span className="text-sm">{file.filename}</span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex flex-wrap gap-3 pt-3">
                            <button
                              onClick={() => openSubmissionDetailsModal(studentSubmission)}
                              className="btn btn-primary btn-sm inline-flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-2" />
                              View Grade & Status
                            </button>
                            {canDownloadCertificate && (
                              <CertificateDownload
                                courseId={courseId}
                                studentData={studentSubmission}
                              />
                            )}
                          </div>
                        </div>

                        {/* Note about one-time submission */}
                        {studentSubmission.status !== 'approved' && (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <div className="flex">
                              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                              <div className="text-sm text-yellow-800">
                                <p className="font-medium">Submission Already Completed</p>
                                <p>You have already submitted this assignment. You cannot make changes or resubmit.</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Certificate Download Info */}
                        {studentSubmission?.status === 'approved' && !canDownloadCertificate && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex">
                              <DocumentArrowDownIcon className="h-5 w-5 text-blue-400 mr-3" />
                              <div className="text-sm text-blue-800">
                                <p className="font-medium mb-2">Certificate Eligibility</p>
                                <p className="mb-1">Congratulations! You have successfully completed the certificate assignment.</p>
                                <p>Your instructor will review your submission and approve certificate download access once all requirements are met. You'll receive notification when your certificate becomes available.</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Ready to submit?</h3>
                        <p className="mt-1 text-sm text-gray-500">
                          Click below to submit your certificate assignment
                        </p>
                        <button
                          onClick={openSubmissionModal}
                          className="mt-4 btn btn-primary"
                        >
                          Submit Assignment
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  // Course not 100% complete
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-center">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Assignments Not Yet Available</h3>
                      <p className="text-gray-600 mb-4">
                        Complete 100% of your course materials before accessing assignments and certificate downloads.
                      </p>
                      <div className="bg-white p-3 rounded border">
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-sm text-gray-600">Current Progress:</span>
                          <span className="font-semibold text-blue-600">{courseProgress ? courseProgress.overallProgress.toFixed(1) : 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Instructor/Admin View */}
            {canManageAssignments && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3">
                  All Submissions ({allSubmissions.length})
                </h4>
                <div className="space-y-3">
                  {allSubmissions.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No submissions yet</p>
                  ) : (
                    allSubmissions.map((submission) => (
                      <div key={submission._id} className="border rounded-lg p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">
                                {submission.student.firstName} {submission.student.lastName}
                              </h5>
                              <p className="text-sm text-gray-600">
                                Submitted {new Date(submission.submittedAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(submission.status)}`}>
                                {submission.status}
                              </span>
                              <button
                                onClick={() => openSubmissionDetailsModal(submission)}
                                className="btn btn-secondary btn-sm"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => openGradingModal(submission)}
                                className="btn btn-primary btn-sm"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Preview submission content */}
                          {/* {submission.submissionText && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-sm font-medium text-gray-700 mb-1">Response:</p>
                              <p className="text-sm text-gray-600 line-clamp-3">{submission.submissionText}</p>
                            </div>
                          )}
                          
                          {submission.attachments?.length > 0 && (
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-sm font-medium text-gray-700 mb-1">Files ({submission.attachments.length}):</p>
                              <div className="flex flex-wrap gap-1">
                                {submission.attachments.map((file, idx) => (
                                  <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    {file.originalName}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )} */}
                        </div>

                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-center">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Assignments Not Yet Available</h3>
                      <p className="text-gray-600 mb-4">
                        Complete 100% of your course materials before accessing assignments and certificate downloads.
                      </p>
                      <div className="bg-white p-3 rounded border">
                        <div className="flex items-center justify-center space-x-2">
                          <span className="text-sm text-gray-600">Current Progress:</span>
                          <span className="font-semibold text-blue-600">{courseProgress ? courseProgress.overallProgress.toFixed(1) : 0}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
        )}
      </div>

      {/* Submission Modal */}
      {showSubmissionModal && (
        <Modal onClose={() => setShowSubmissionModal(false)}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Submit Certificate Assignment</h2>

            {/* Assignment Info */}
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-blue-900">{assignment.title}</h3>
              <p className="text-sm text-blue-800 mt-1">{assignment.description}</p>
              <p className="text-sm text-blue-700 mt-2">
                Passing Score: {assignment.passingScore}% or higher
              </p>
            </div>

            {/* Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Important Warning</p>
                  <p>This assignment can only be submitted <strong>ONCE</strong>. You cannot resubmit after submission. Make sure your work is complete.</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>

                  <label className="block text-sm font-medium text-gray-700">Submission Text</label>
                  <textarea
                    value={submissionText}
                    onChange={(e) => setSubmissionText(e.target.value)}
                    rows={6}
                    className="input mt-1 w-full"
                    placeholder="Describe your submission, approach, and any additional notes..."
                  />
                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700">Attachments (optional)</label>
                  <div className="mt-1">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="modal-file-upload"
                      accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                    />
                    <label
                      htmlFor="modal-file-upload"
                      className="btn btn-secondary btn-sm inline-flex items-center cursor-pointer"
                    >
                      <PaperClipIcon className="h-4 w-4 mr-2" />
                      Choose Files (max 5, 10MB each)
                    </label>
                    {attachments.length > 0 && (
                      <div className="mt-2 text-sm text-gray-600">
                        Selected: {attachments.map(f => f.name).join(', ')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSubmissionModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!submissionText.trim() && attachments.length === 0}
                    className="btn btn-primary"
                  >
                    Submit Assignment
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Grading Modal */}
      {showGradingModal && selectedSubmission && (
        <Modal onClose={() => setShowGradingModal(false)}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Grade Submission - {selectedSubmission.student.firstName} {selectedSubmission.student.lastName}
            </h2>

            <form onSubmit={handleGrade}>
              <div className="space-y-4">
                <div>

                  <label className="block text-sm font-medium text-gray-700">Grade (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={gradingForm.percentage}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, percentage: e.target.value }))}
                    className="input mt-1"
                    placeholder={`Min passing: ${assignment.passingScore}%`}
                    required
                  />
                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700">Feedback</label>
                  <textarea
                    value={gradingForm.feedback}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, feedback: e.target.value }))}
                    rows={4}
                    className="input mt-1"
                    placeholder="Provide detailed feedback for the student..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allow-certificate"
                    checked={gradingForm.allowCertificateDownload}
                    onChange={(e) => setGradingForm(prev => ({ ...prev, allowCertificateDownload: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="allow-certificate" className="ml-2 block text-sm text-gray-900">
                    Allow student to download certificate
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowGradingModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Submit Grade
                  </button>
                </div>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Submission Details Modal */}
      {showSubmissionDetailsModal && selectedSubmission && (
        <Modal onClose={() => setShowSubmissionDetailsModal(false)}>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Submission Details - {selectedSubmission.student.firstName} {selectedSubmission.student.lastName}
            </h2>

            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${getStatusColor(selectedSubmission.status)}`}>
                <p className="font-medium">Status: {selectedSubmission.status}</p>
                <p className="text-sm">Submitted: {new Date(selectedSubmission.submittedAt).toLocaleDateString()}</p>
              </div>

              {selectedSubmission.submissionText && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Submission Text</h3>
                  <div className="bg-gray-50 p-4 rounded border max-h-60 overflow-y-auto">
                    <p className="whitespace-pre-wrap">{selectedSubmission.submissionText}</p>
                  </div>
                </div>
              )}

              {selectedSubmission.attachments?.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">Attachments</h3>
                  <div className="space-y-2">
                    {selectedSubmission.attachments.map((file, index) => (
                      <a
                        key={index}
                        href={`${file.path}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center space-x-3 bg-blue-50 text-blue-700 px-4 py-2 rounded border hover:bg-blue-100 transition-colors"
                      >
                        <PaperClipIcon className="h-4 w-4" />
                        <span>{file.filename}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {selectedSubmission.grade && (
                <div className="bg-green-50 border border-green-200 rounded p-4">
                  <h3 className="font-medium text-green-900 mb-2">Grade Information</h3>
                  <p className="font-medium">Grade: {selectedSubmission.grade.percentage}% ({selectedSubmission.grade.letterGrade})</p>
                  {selectedSubmission.feedback && (
                    <p className="text-sm mt-2">Feedback: {selectedSubmission.feedback}</p>
                  )}
                  {user?.role != 'student' && (
                    <>
                      <p className="text-sm text-gray-600 mt-1">
                        Certificate Download: {selectedSubmission.allowCertificateDownload ? 'Allowed' : 'Not Allowed'}
                      </p>
                      <p className="text-sm text-gray-600">
                        Graded by: {selectedSubmission.grade.gradedBy?.firstName} {selectedSubmission.grade.gradedBy?.lastName}
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Modal Component
const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 overflow-y-auto">
    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div className="fixed inset-0 transition-opacity" aria-hidden="true">
        <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
      </div>

      <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
        <div className="absolute top-0 right-0 pt-4 pr-4">
          <button
            onClick={onClose}
            className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  </div>
);

export default CertificateAssignment;
